import { useMemo } from "react";
import { type MinimalQueryResult } from "@jsq/ds/types";
import { useDebouncedValue } from "~/common/hooks/useDebouncedValue";
import { useRankedProfilesQuery } from "~/graphql/supergraphHooks";
import { useCaseInvestmentAccount } from "../../useCaseInvestmentAccount";
import { deriveProfileMode, deriveSearchArgs, rankedProfileToListItem } from "./utils";
import type { ProfileListItem } from "./ProfileSelection/types";
import type { ProfileExtraOption } from "./utils";
import type { WizardCaseInfo } from "../../types";

const DEBOUNCE_MS = 300;

export const useRankedProfiles = ({
  caseInfo,
  searchValue,
  forceSearch,
}: {
  caseInfo: WizardCaseInfo | null;
  searchValue: string;
  forceSearch: boolean;
}): {
  profiles: ProfileListItem[];
  isSearchHidden: boolean;
  isSearching: boolean;
  extraOption: ProfileExtraOption;
  queryResults: MinimalQueryResult[];
  arenaId: number | undefined;
  entityId: number | undefined;
  investmentAccountId: number | null;
  investmentAccountSettled: boolean;
  canReturnToAccountList: boolean;
} => {
  const { arenaId, entityId, investmentAccountId, resolveState, resolveSettled } =
    useCaseInvestmentAccount(caseInfo);
  const investmentAccountResolved = investmentAccountId !== null;
  const baseSkip = arenaId === undefined || entityId === undefined;

  // Fetch the IA-ranked list whenever an IA exists (even after "None of the above") so we always
  // know if the IA has associated profiles — that gates both the list mode and the create option.
  const iaResult = useRankedProfilesQuery({
    variables: {
      arenaId: arenaId ?? 0,
      entityId: entityId ?? 0,
      investmentAccountId: investmentAccountId ?? undefined,
    },
    skip: baseSkip || !investmentAccountResolved,
    fetchPolicy: "cache-and-network",
  });
  const iaProfilesRaw = useMemo(
    () => iaResult.data?.rankedProfile.profiles ?? [],
    [iaResult.data],
  );
  const iaQuerySettled = !investmentAccountResolved || (iaResult.called && !iaResult.loading);

  const { mode, isSearchHidden, extraOption, canReturnToAccountList } = deriveProfileMode({
    resolveSettled,
    investmentAccountResolved,
    forceSearch,
    iaQuerySettled,
    hasIaProfiles: iaProfilesRaw.length > 0,
  });

  const debouncedSearch = useDebouncedValue(searchValue, DEBOUNCE_MS);
  const searchArgs = useMemo(() => deriveSearchArgs(debouncedSearch), [debouncedSearch]);

  const searchResult = useRankedProfilesQuery({
    variables: {
      arenaId: arenaId ?? 0,
      entityId: entityId ?? 0,
      ...(investmentAccountId !== null && { investmentAccountId }),
      taxId: searchArgs.taxId,
      name: searchArgs.name,
    },
    skip: baseSkip || mode !== "search" || searchArgs.matchedBy === null,
    fetchPolicy: "cache-and-network",
  });

  const profiles = useMemo(() => {
    if (mode === "investment-account") {
      return iaProfilesRaw.map((profile) => rankedProfileToListItem(profile, null));
    }
    // Apollo retains the prior result's data when a query is skipped, so guard on the
    // search args here — otherwise clearing (or shortening) the box shows stale matches.
    if (mode !== "search" || searchArgs.matchedBy === null) return [];
    const raw = searchResult.data?.rankedProfile.profiles ?? [];
    return raw.map((profile) => rankedProfileToListItem(profile, searchArgs.matchedBy));
  }, [mode, iaProfilesRaw, searchResult.data, searchArgs.matchedBy]);

  // The pending debounce window counts as searching: otherwise the list keeps showing the
  // previous query's matches for 300ms with no indication that they're stale.
  const isSearching =
    mode === "search" &&
    deriveSearchArgs(searchValue).matchedBy !== null &&
    (searchResult.loading || debouncedSearch !== searchValue);

  // Only the initial resolution gates the page. The live search query updates the list in
  // place, so it must stay out of the page-level loader or every keystroke blanks the panel.
  const queryResults = [resolveState, iaResult];

  return {
    profiles,
    isSearchHidden,
    isSearching,
    extraOption,
    queryResults,
    arenaId,
    entityId,
    investmentAccountId,
    investmentAccountSettled: resolveSettled,
    canReturnToAccountList,
  };
};
